/**
 * Qué le dice la app al cliente sobre el cobro de su suscripción
 * (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). Es la ÚNICA decisión: la home, el header
 * mobile y /subscription la consumen en vez de comparar `trigger_cancelacion` cada uno por su lado.
 *
 *  - "rechazo" (A): se rechazó la renovación y el procesador todavía reintenta.
 *  - "baja_programada" (B): se agotaron los reintentos (o MP rechazó la renovación) y la baja está
 *    agendada; puede regularizar antes de la fecha.
 *  - "dado_de_baja" (C): el plan se dio de baja por falta de pago.
 *
 * Las columnas `cobro_*` las escribe el payment server (ADR-0030 de ese repo).
 */

/** `trigger_cancelacion` que marcan una baja por cobro fallido. Cualquier otro valor (o null) es una baja pedida. */
export const MOTIVOS_DE_COBRO_FALLIDO = [
  "fondos_insuficientes",
  "renovacion_rechazada",
  "mp_paused_dunning",
  "rebill_defaulted",
  "stripe_impago",
] as const;

export function esMotivoDeCobroFallido(trigger: string | null | undefined): boolean {
  return (MOTIVOS_DE_COBRO_FALLIDO as readonly string[]).includes(trigger ?? "");
}

export type MotivoRechazo = "fondos" | "tarjeta_vencida" | "banco" | "otro";
export type PlataformaDePago = "mp" | "rebill" | "stripe" | null;

/**
 * Facturación por marca (ADR-2026-10-29-cobro-rechazado-por-marca): el cobro rechazado es el del
 * plan de una MARCA, no el de la empresa. Sin esto, el aviso es el de la empresa, como siempre.
 */
export interface MarcaDelCobro {
  brandId: string;
  nombre: string;
  /** Sólo el dueño cambia la tarjeta; un miembro ve "pedile al admin de la cuenta". */
  esDueno: boolean;
  /** Las demás marcas con el cobro rechazado: el aviso nombra la más urgente y menciona éstas. */
  otras: string[];
}

export type EstadoDeCobro =
  | {
      tipo: "rechazo";
      plataforma: PlataformaDePago;
      motivo: MotivoRechazo | null;
      /** null = no se sabe cuándo (o ya pasó). */
      proximoReintento: Date | null;
      /** Últimos 4 dígitos de la tarjeta rechazada; null si el procesador no los mandó. */
      tarjeta: string | null;
      marca?: MarcaDelCobro;
      /** TEMPORAL: estado de `lib/simulacionDeCobro` (sus botones no hacen nada). */
      simulado?: true;
    }
  | {
      tipo: "baja_programada";
      plataforma: PlataformaDePago;
      motivo: MotivoRechazo | null;
      fechaBaja: Date;
      tarjeta: string | null;
      marca?: MarcaDelCobro;
      simulado?: true;
    }
  | {
      tipo: "dado_de_baja";
      plataforma: PlataformaDePago;
      /** Para recordar que el usuario cerró ESTE aviso (otra baja futura vuelve a mostrarse). */
      claveCierre: string;
      simulado?: true;
    };

/** Las columnas de `company_profiles` que hacen falta. Las `cobro_*` pueden no venir (consulta aparte). */
export interface EmpresaParaCobro {
  id_company: string;
  plan: string | null;
  plataforma_pago: string | null;
  trigger_cancelacion: string | null;
  fecha_cancelacion_programada: string | null;
  paid_plan_cancel_date: string | null;
  cobro_rechazado_at?: string | null;
  cobro_proximo_reintento?: string | null;
  cobro_motivo_rechazo?: string | null;
  cobro_tarjeta_ultimos4?: string | null;
}

/**
 * Cuánto se muestra el aviso de "te dimos de baja". Cuenta desde `paid_plan_cancel_date`, que se
 * escribe al AGENDAR la baja (hasta un mes antes de ejecutarla): por eso 90 días y no menos.
 */
const VENTANA_DADO_DE_BAJA_MS = 90 * 86_400_000;

const MOTIVOS: readonly MotivoRechazo[] = ["fondos", "tarjeta_vencida", "banco", "otro"];

function fecha(valor: string | null | undefined): Date | null {
  if (!valor) return null;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

function motivo(valor: string | null | undefined): MotivoRechazo | null {
  return MOTIVOS.includes(valor as MotivoRechazo) ? (valor as MotivoRechazo) : null;
}

function tarjeta(valor: string | null | undefined): string | null {
  return valor && /^\d{4}$/.test(valor) ? valor : null;
}

function plataforma(valor: string | null): PlataformaDePago {
  const p = valor?.trim().toLowerCase();
  return p === "mp" || p === "rebill" || p === "stripe" ? p : null;
}

export function estadoDeCobro(
  empresa: EmpresaParaCobro,
  ahora: Date = new Date(),
): EstadoDeCobro | null {
  const pago = !!empresa.plan && empresa.plan !== "gratis";
  const fallido = esMotivoDeCobroFallido(empresa.trigger_cancelacion);
  const plat = plataforma(empresa.plataforma_pago);

  if (pago) {
    const fechaBaja = fecha(empresa.fecha_cancelacion_programada);
    // Sin exigir que la fecha sea futura: entre que vence y corre el cron (hasta 1 h) el plan sigue
    // pago y la baja sigue siendo por falta de pago, con sus acciones (Pagar ahora), no la de una
    // baja pedida. Hallazgo de Greptile, PR #208.
    if (fallido && fechaBaja) {
      return {
        tipo: "baja_programada",
        plataforma: plat,
        motivo: motivo(empresa.cobro_motivo_rechazo),
        fechaBaja,
        tarjeta: tarjeta(empresa.cobro_tarjeta_ultimos4),
      };
    }
    // Una baja voluntaria agendada manda sobre un rechazo suelto: ahí el aviso es el de siempre.
    if (fecha(empresa.cobro_rechazado_at) && !fechaBaja) {
      const proximo = fecha(empresa.cobro_proximo_reintento);
      return {
        tipo: "rechazo",
        plataforma: plat,
        motivo: motivo(empresa.cobro_motivo_rechazo),
        proximoReintento: proximo && proximo > ahora ? proximo : null,
        tarjeta: tarjeta(empresa.cobro_tarjeta_ultimos4),
      };
    }
    return null;
  }

  const baja = fecha(empresa.paid_plan_cancel_date);
  if (fallido && baja && ahora.getTime() - baja.getTime() <= VENTANA_DADO_DE_BAJA_MS) {
    return {
      tipo: "dado_de_baja",
      plataforma: plat,
      claveCierre: `${empresa.id_company}:${empresa.paid_plan_cancel_date}`,
    };
  }
  return null;
}

// ─── Facturación por marca ───────────────────────────────────────────────────

/**
 * ¿Hay que volver a leer el aviso porque recién ahora se sabe que la empresa factura por marca? Sólo
 * cuando la última lectura no lo sabía y el saldo que llegó dice que sí: una empresa sin la bandera
 * nunca relee (ADR-2026-10-29-cobro-rechazado-por-marca-2).
 */
export function hayQueReleerPorLaBandera(
  leyoConBandera: boolean,
  ultimoSaldo: { facturaPorMarca: boolean } | null | undefined,
): boolean {
  return !leyoConBandera && ultimoSaldo?.facturaPorMarca === true;
}

/** Las columnas de `billeteras_de_marca` que hacen falta (las escribe el webhook de marca del payment server). */
export interface BilleteraParaCobro {
  brand_id: string;
  plan: string | null;
  trigger_cancelacion: string | null;
  fecha_cancelacion_programada: string | null;
  cobro_rechazado_at?: string | null;
  cobro_proximo_reintento?: string | null;
  cobro_motivo_rechazo?: string | null;
  cobro_tarjeta_ultimos4?: string | null;
}

/** Un cobro que todavía se puede regularizar (lo único que puede pasarle a una marca). */
export type CobroPendiente = Extract<EstadoDeCobro, { tipo: "rechazo" | "baja_programada" }>;

/**
 * El estado de cobro de UNA billetera de marca, con la MISMA decisión que la empresa: la morosidad de
 * Rebill (`rebill_defaulted` + baja agendada, billetera `paused`) es la "baja_programada" y un
 * rechazo con reintento es "rechazo". Una baja pedida no es un cobro rechazado. Las marcas cobran
 * siempre por Rebill. Sin plan no hay nada que avisar (no existe el "dado de baja" de una marca).
 */
export function estadoDeCobroDeBilletera(b: BilleteraParaCobro, ahora: Date = new Date()): CobroPendiente | null {
  if (!b.plan) return null;
  const e = estadoDeCobro(
    {
      id_company: b.brand_id,
      plan: b.plan,
      plataforma_pago: "rebill",
      trigger_cancelacion: b.trigger_cancelacion,
      fecha_cancelacion_programada: b.fecha_cancelacion_programada,
      paid_plan_cancel_date: null,
      cobro_rechazado_at: b.cobro_rechazado_at,
      cobro_proximo_reintento: b.cobro_proximo_reintento,
      cobro_motivo_rechazo: b.cobro_motivo_rechazo,
      cobro_tarjeta_ultimos4: b.cobro_tarjeta_ultimos4,
    },
    ahora,
  );
  return e && e.tipo !== "dado_de_baja" ? e : null;
}

/**
 * El aviso de cobro de una empresa con facturación por marca: el de la marca más urgente (la baja
 * más cercana primero, después los rechazos con reintento, por nombre), con las demás nombradas en
 * `marca.otras`. `null` si ninguna marca tiene un cobro rechazado.
 */
export function estadoDeCobroDeMarcas(
  billeteras: BilleteraParaCobro[],
  nombres: ReadonlyMap<string, string>,
  esDueno: boolean,
  ahora: Date = new Date(),
): EstadoDeCobro | null {
  const conProblema = billeteras
    .map((b) => ({ b, e: estadoDeCobroDeBilletera(b, ahora), nombre: nombres.get(b.brand_id) ?? "una de tus marcas" }))
    .filter((x): x is { b: BilleteraParaCobro; e: CobroPendiente; nombre: string } => x.e !== null)
    .sort((x, y) => {
      if (x.e.tipo !== y.e.tipo) return x.e.tipo === "baja_programada" ? -1 : 1;
      if (x.e.tipo === "baja_programada" && y.e.tipo === "baja_programada") {
        const d = x.e.fechaBaja.getTime() - y.e.fechaBaja.getTime();
        if (d !== 0) return d;
      }
      return x.nombre.localeCompare(y.nombre, "es");
    });
  const [principal, ...resto] = conProblema;
  if (!principal) return null;
  return {
    ...principal.e,
    marca: { brandId: principal.b.brand_id, nombre: principal.nombre, esDueno, otras: resto.map((x) => x.nombre) },
  };
}
